@if (initialLoadFailed() && loadError(); as error) {
  <tf-settings-load-error
    [title]="error"
    [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
    [details]="loadErrorDetails()"
    [retrying]="isReloading()"
    (retry)="reload()" />
} @else if (!hasLoadedOnce()) {
  <div class="areas-grid" aria-busy="true">
    @for (_ of skeletonCount; track $index) {
      <tf-settings-card class="area-card-skeleton">
        <div settingsCardPreview class="map-thumb"></div>
        <div settingsCardContent class="area-card-skeleton__body">
          <div class="skel-row">
            <span class="skel-circle"></span>
            <span class="skel-bar w-60"></span>
          </div>
          <span class="skel-bar w-80"></span>
        </div>
        <tf-settings-card-actions>
          <span settingsCardActionsStart class="skel-bar w-40"></span>
        </tf-settings-card-actions>
      </tf-settings-card>
    }
  </div>
} @else if (showEmptyState()) {
  <tf-settings-inline-message
    [message]="actionError()"
    messageId="areas-action-error"
    (dismissed)="clearActionError()" />

  <tf-empty-state
    [title]="'SETTINGS_AREAS.EMPTY.TITLE' | translate"
    [body]="'SETTINGS_AREAS.EMPTY.BODY' | translate">
    <svg
      empty-state-illustration
      class="area-illo"
      width="92"
      height="92"
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true">
      <g class="basemap-line" stroke="currentColor" stroke-width="0.7" opacity="0.6">
        <line x1="6" y1="22" x2="94" y2="22" />
        <line x1="6" y1="44" x2="94" y2="44" />
        <line x1="6" y1="66" x2="94" y2="66" />
        <line x1="6" y1="88" x2="94" y2="88" />
        <line x1="22" y1="6" x2="22" y2="94" />
        <line x1="44" y1="6" x2="44" y2="94" />
        <line x1="66" y1="6" x2="66" y2="94" />
        <line x1="88" y1="6" x2="88" y2="94" />
      </g>
      <path class="poly-fill" d="M28,30 L62,22 L82,40 L78,68 L46,82 L22,58 Z" />
      <path class="poly-stroke" d="M28,30 L62,22 L82,40 L78,68 L46,82 L22,58 Z" />
      <circle class="vertex" cx="28" cy="30" r="3" />
      <circle class="vertex" cx="62" cy="22" r="3" />
      <circle class="vertex" cx="82" cy="40" r="3" />
      <circle class="vertex" cx="78" cy="68" r="3" />
      <circle class="vertex" cx="46" cy="82" r="3" />
      <circle class="vertex" cx="22" cy="58" r="3" />
    </svg>
    <p-button
      empty-state-actions
      icon="pi pi-plus"
      [label]="'SETTINGS_AREAS.ACTIONS.CREATE' | translate"
      [disabled]="!canCreateArea()"
      (onClick)="createArea()" />
    <p-button
      empty-state-actions
      icon="pi pi-refresh"
      severity="secondary"
      [loading]="isReloading()"
      [disabled]="isReloading()"
      [ariaLabel]="'GENERAL.REFRESH' | translate"
      (onClick)="reload()" />
  </tf-empty-state>
} @else {
  <tf-settings-list-header
    [searchQuery]="searchQuery()"
    [searchPlaceholder]="'SETTINGS_AREAS.SEARCH_PLACEHOLDER' | translate"
    [loading]="isReloading()"
    [refreshTooltip]="'GENERAL.REFRESH' | translate"
    (searchChange)="onSearchChange($event)"
    (refresh)="reload()">
    <div
      headerCenter
      class="view-toggle"
      role="tablist"
      [attr.aria-label]="'SETTINGS_AREAS.VIEW_LABEL' | translate">
      <button
        type="button"
        role="tab"
        class="view-toggle__btn"
        [class.active]="view() === 'cards'"
        [attr.aria-selected]="view() === 'cards'"
        (click)="setView('cards')">
        <i class="pi pi-th-large" aria-hidden="true"></i>
        <span>{{ 'SETTINGS_AREAS.VIEW.CARDS' | translate }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="view-toggle__btn"
        [class.active]="view() === 'table'"
        [attr.aria-selected]="view() === 'table'"
        (click)="setView('table')">
        <i class="pi pi-list" aria-hidden="true"></i>
        <span>{{ 'SETTINGS_AREAS.VIEW.TABLE' | translate }}</span>
      </button>
    </div>
    <p-button
      headerActions
      icon="pi pi-plus"
      [label]="'SETTINGS_AREAS.ACTIONS.CREATE' | translate"
      [disabled]="!canCreateArea()"
      (onClick)="createArea()" />
  </tf-settings-list-header>

  <tf-settings-inline-message
    [message]="actionError()"
    messageId="areas-action-error"
    (dismissed)="clearActionError()" />

  @if (view() === 'cards') {
    <div class="areas-grid">
      @for (area of visibleAreas(); track area.id) {
        <tf-settings-card [title]="area.name" [subtitle]="area.description">
          <div settingsCardPreview class="map-thumb">
            <tf-settings-area-mini-map
              [color]="area.color ?? '#94A3B8'"
              [geoJson]="area.geoJson"
              [ariaLabel]="(area.name ?? '') + ' polygon'" />
          </div>
          @if ((area.zipRanges?.length ?? 0) > 0) {
            <div settingsCardContent class="zip-chips">
              @for (range of visibleZipRanges(area); track $index) {
                <span class="zip-chip">{{ formatZipRange(range) }}</span>
              }
              @if (hiddenZipRangeCount(area); as more) {
                <span class="zip-chip zip-chip--more">+{{ more }}</span>
              }
            </div>
          }
          <tf-settings-card-actions>
            <span settingsCardActionsStart class="timestamp">
              {{ 'SETTINGS_AREAS.UPDATED.LABEL' | translate }} {{ relativeUpdated(area) }}
            </span>
            <span settingsCardActionsEnd class="actions">
              <p-button
                icon="pi pi-pencil"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                size="small"
                [disabled]="!canUpdateArea()"
                [attr.aria-label]="'SETTINGS_AREAS.ACTIONS.EDIT' | translate"
                [pTooltip]="'SETTINGS_AREAS.ACTIONS.EDIT' | translate"
                tooltipPosition="top"
                (onClick)="editArea(area)" />
              <p-button
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                [rounded]="true"
                size="small"
                [disabled]="!canDeleteArea()"
                [attr.aria-label]="'SETTINGS_AREAS.ACTIONS.DELETE' | translate"
                [pTooltip]="'SETTINGS_AREAS.ACTIONS.DELETE' | translate"
                tooltipPosition="top"
                (onClick)="deleteArea(area)" />
            </span>
          </tf-settings-card-actions>
        </tf-settings-card>
      }

      @if (canCreateArea() && visibleAreas().length > 0) {
        <tf-settings-card-new
          [title]="'SETTINGS_AREAS.ACTIONS.CREATE' | translate"
          [hint]="'SETTINGS_AREAS.ACTIONS.CREATE_HINT' | translate"
          (cardClick)="createArea()" />
      }
    </div>
  } @else {
    <tf-data-table
      [columns]="tableColumns"
      [value]="visibleAreasForTable()"
      [loading]="false"
      [paginator]="true"
      [rows]="25"
      [totalRecords]="visibleAreasForTable().length"
      [currentPageReportTemplate]="'SETTINGS_AREAS.PAGINATION_MESSAGE' | translate"
      styleClass="p-datatable-sm"
      emptyMessageKey="SETTINGS_AREAS.EMPTY.TITLE">
      <ng-template tfColumnDef="_thumb" let-area>
        <div class="areas-table__thumb">
          <tf-settings-area-mini-map
            [color]="area.color ?? '#94A3B8'"
            [geoJson]="area.geoJson"
            [ariaLabel]="(area.name ?? '') + ' polygon'" />
        </div>
      </ng-template>
      <ng-template tfColumnDef="_name" let-area>
        <div class="areas-table__name">
          <span class="swatch" [style.background]="area.color ?? '#94A3B8'"></span>
          <span class="stack">
            <span class="name" [title]="area.name">{{ area.name }}</span>
            @if (area.description) {
              <span class="desc" [title]="area.description">{{ area.description }}</span>
            }
          </span>
        </div>
      </ng-template>
      <ng-template tfColumnDef="_zipRanges" let-area>
        @if ((area.zipRanges?.length ?? 0) > 0) {
          <div class="zip-chips">
            @for (range of visibleZipRanges(area); track $index) {
              <span class="zip-chip">{{ formatZipRange(range) }}</span>
            }
            @if (hiddenZipRangeCount(area); as more) {
              <span class="zip-chip zip-chip--more">+{{ more }}</span>
            }
          </div>
        } @else {
          <span class="zip-chips__empty">—</span>
        }
      </ng-template>
      <ng-template tfColumnDef="_actions" let-area>
        <div class="areas-table__actions">
          <p-button
            icon="pi pi-pencil"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canUpdateArea()"
            [attr.aria-label]="'SETTINGS_AREAS.ACTIONS.EDIT' | translate"
            [pTooltip]="'SETTINGS_AREAS.ACTIONS.EDIT' | translate"
            tooltipPosition="top"
            (onClick)="editArea(area)" />
          <p-button
            icon="pi pi-trash"
            severity="danger"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canDeleteArea()"
            [attr.aria-label]="'SETTINGS_AREAS.ACTIONS.DELETE' | translate"
            [pTooltip]="'SETTINGS_AREAS.ACTIONS.DELETE' | translate"
            tooltipPosition="top"
            (onClick)="deleteArea(area)" />
        </div>
      </ng-template>
    </tf-data-table>
  }
}

<tf-settings-area-form-dialog
  [visible]="isDialogVisible()"
  [editingArea]="editingArea()"
  [saving]="isSaving()"
  (visibleChange)="onDialogVisibilityChange($event)"
  (cancelled)="onDialogCancel()"
  (save)="onDialogSave($event)" />

<p-confirmDialog />
